import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy,
  Output, SimpleChanges, ViewChild
} from '@angular/core';
import { MenuItem } from 'primeng-lts/api';
import { fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { FieldConfig, FieldGroup, IEvaluableExecutor } from '../../../api/contract-notice-dto';
import { BaseComponent } from '../../common/baseComponent/base.component';
import { FunctionDef } from '../form.objects';
import { FormService } from '../form.service';


/**
 * Item de menú asociado a un grupo
 */
export interface GroupMenuItem extends MenuItem {
  group: FieldGroup;
  items: MenuItem[];
}

/**
 * Item de menú asociado a un componente de tipo TITLE
 */
export interface TitleMenuItem extends MenuItem {
  field: FieldConfig;
  items: FieldMenuItem[];
}

/**
 * Item de menú asociado a un componente que no sea de tipo TITLE
 * Este
 */
export interface FieldMenuItem extends MenuItem {
  field: FieldConfig;
}

@Component({
  selector: 'form-index',
  templateUrl: './form-index.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormIndexComponent extends BaseComponent implements IEvaluableExecutor, AfterViewInit, OnDestroy, OnChanges {

  public MIN_SEARCH_TEXT_LENGHT = 3;

  @Input()
  fieldGroups: FieldGroup[];
  /**
   * Flag para indicar que se debe regenerar el indice
   */
  @Input()
  renewIndex: boolean;
  @Input()
  model: any;
  @Input()
  functions: Array<FunctionDef>;
  @Output()
  selectedField = new EventEmitter<FieldConfig>(undefined);
  @Output()
  selectedGroup = new EventEmitter<FieldGroup>(undefined);

  @ViewChild('searchField')
  searchElement: ElementRef;

  public menuItems: Array<MenuItem>;
  public userFilter: string;
  private filter: string;

  constructor(public formService: FormService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      let renewIndexChanges = changes['renewIndex'];
      if (renewIndexChanges && renewIndexChanges.previousValue !== renewIndexChanges.currentValue) {
        this.createMenuItems();
      }
    }
  }

  ngOnDestroy() {
    this.fieldGroups = null;
    super.ngOnDestroy();
  }

  ngAfterViewInit() {
    this.subscribe(fromEvent(this.searchElement.nativeElement, 'input')
      .pipe(
        filter(Boolean),
        debounceTime(150),
        distinctUntilChanged(),
        tap((query: string) => {
          let prevFilter = this.filter;
          if (this.searchElement.nativeElement.value.length > this.MIN_SEARCH_TEXT_LENGHT) {
            this.filter = this.userFilter;
          } else {
            this.filter = undefined;
          }
          if (prevFilter !== this.filter) {
            this.createMenuItems();
          }
        })
      ));
    this.createMenuItems();
  }

  public createMenuItems() {
    this.menuItems = new Array<MenuItem>();
    if (this.fieldGroups && this.fieldGroups.length > 0) {
      if (this.fieldGroups.length > 1) {
        let groupItems = this.createGroupItems(this.fieldGroups);
        // aplanar los grupos con solo un TitleMenuItem
        groupItems.forEach((groupItem: GroupMenuItem) => {
          if (groupItem.items.length > 1) {
            // el grupo tiene varios submenus se deja tal cual
            this.menuItems.push(groupItem);
          } else if (groupItem.items.length === 1) {
            let firstMenuItem = groupItem.items[0];
            if (firstMenuItem.items) {
              // tenemos un unico campo de tipo TitleMenuItem
              // quitamos el TitleMenuItem y dejamos los FieldMenuItem como parte del GroupMenuItem
              groupItem.items = firstMenuItem.items as MenuItem[];
            }
            this.menuItems.push(groupItem);
          }
        });
      } else {
        this.fieldsToMenuItem(this.fieldGroups[0].fieldList).forEach((item) => this.menuItems.push(item));
      }
    }
  }

  public clearFilter(): void {
    this.userFilter = undefined;
    this.filter = undefined;
    this.createMenuItems();
  }

  private createGroupItems(groups: FieldGroup[]): MenuItem[] {
    let groupItems = new Array<MenuItem>();
    groups.forEach((group: FieldGroup) => groupItems.push(this.groupToMenuItem(group)));
    return groupItems;
  }

  private groupToMenuItem(group: FieldGroup): GroupMenuItem {
    let menuItem = {} as GroupMenuItem;
    menuItem.label = this.formService.getGroupDescription(this, group);
    menuItem.group = group;
    menuItem.expanded = this.filter !== undefined;
    menuItem.items = this.fieldsToMenuItem(group.fieldList);
    return menuItem;
  }

  private fieldsToMenuItem(fieldList: FieldConfig[]): Array<MenuItem> {
    let fieldItems = new Array<TitleMenuItem | FieldMenuItem>();
    let titleItem: TitleMenuItem;
    fieldList.forEach((field: FieldConfig) => {
      if (field.type === 'TITLE') {
        titleItem = this.fieldToTitleMenuItem(field);
        titleItem.items = [];
        titleItem.expanded = this.filter !== undefined;
        fieldItems.push(titleItem);
      } else if (field.type !== 'LABEL' && field.type !== 'HIDDEN') {
        try {
          const fieldItem = this.fieldToMenuItem(field);
          // se añaden campos con etiqueta
          // que sea visible (por lo tanto tenga un campo en el formulario)
          // y si hay filtros los que lo cumplan
          let addField = fieldItem.label !== undefined &&
            field.formElement?.isVisible() &&
            (!this.filter
              ||
              fieldItem.label.toLowerCase().indexOf(this.filter.toLowerCase()) > -1);
          if (addField) {
            if (!titleItem) {
              // añadir campo a la lista del grupo
              fieldItem.styleClass = 'no-title';
              fieldItems.push(fieldItem);
            } else {
              // añadir campo bajo el último TitleMenuItem
              titleItem.items.push(fieldItem);
            }
          }
        } catch (error: any) {
          console.error('Error al transformar campo a item del indice', field, error);
        }
      }
    });
    // filtrar por FieldMenuItem o TitleMenuItem que contenga al menos un MenuItem que cumpla el filtro
    fieldItems = fieldItems.filter((menuItem: MenuItem) => {
      if (!menuItem.items) {
        // tipo FieldMenuItem
        return true;
      } else {
        let submenu = menuItem.items as FieldMenuItem[];
        // tengan etiqueta
        return menuItem.items.length > 0;
      }
    }
    );
    return fieldItems;
  }

  private fieldToTitleMenuItem(field: FieldConfig): TitleMenuItem {
    let menuItem = {} as TitleMenuItem;
    menuItem.label = field.formElement.getDescription();
    menuItem.field = field;
    return menuItem;
  }

  private fieldToMenuItem(field: FieldConfig): FieldMenuItem {
    let menuItem = this.fieldToTitleMenuItem(field) as FieldMenuItem;
    menuItem.command = function (event: { originalEvent: Event, item: TitleMenuItem }) {
      this.selectField(event.item.field);
    }.bind(this);
    switch (field.type) {
      case 'DATE':
      case 'DATETIME': {
        menuItem.icon = 'far fa-calendar-alt';
        break;
      }
      case 'PERCENTAGE': {
        menuItem.icon = 'fas fa-percentage';
        break;
      }
      case 'DROPDOWN':
      case 'MULTISELECT': {
        menuItem.icon = 'fas fa-tasks';
        break;
      }
      case 'DECIMAL':
      case 'INTEGER':
      case 'CURRENCY': {
        menuItem.icon = 'fas fa-euro-sign';
        break;
      }
      case 'FILE': {
        menuItem.icon = 'far fa-file';
        break;
      }
      case 'SEARCH': {
        menuItem.icon = 'fas fa-search';
        break;
      }
      case 'TABLE': {
        menuItem.icon = 'fas fa-table';
        break;
      }
      case 'CHECK':
      case 'YES_NO': {
        menuItem.icon = 'far fa-check-square';
        break;
      }
      default: {
        menuItem.icon = 'far fa-keyboard';
      }
    }
    return menuItem;
  }
  private selectField(field: FieldConfig): void {
    this.selectedField.emit(field);
  }

}
